import { CalendarClock } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BackupPicker, useBackupTime } from "@/features/callback-backup";
import { useCallbackMinutes } from "@/features/callback-minutes";
import { CallbackPicker, useCallbackTime } from "@/features/callback-picker";
import { UpcomingAppointments } from "@/features/upcoming-appointments";
import { backupGapMinutes, backupTimesWhen, callbackWhen, knownZone, wallTime } from "@/lib/callbacks";
import { leadName } from "@/lib/campaign";
import type { LeadCard } from "@/lib/types";
import { forgetPickedCallback, pickCallback, usePickedCallback } from "@/softphone/picked-callback";

/**
 * Scheduling with the lead while they are on the line (ACL-559): an appointment booked from the
 * call screen's card, and the callback's time picked before the call ends.
 *
 * The booking is the one the Calendars page and a conversation make (`NewAppointmentDialog`), with
 * the lead fixed as the contact. It is not an outcome: it sets no stage and no hold, and the dialer
 * calls the lead as it would have.
 *
 * The callback's time picked here records nothing. It is kept for the call (`picked-callback.ts`),
 * and the outcome window opens on it when the rep chooses a callback, so the time still reaches the
 * dialer as that outcome's `callback_at` (ADR-080 in `platform`). Any other outcome makes no
 * callback. It is offered only on the ACD's call with this lead, the one an outcome is asked for:
 * `callId` is that call's caller channel while it is up (`LeadCardPanel`), else null. Backup times
 * picked with it ("4 pm, or else 7 pm, then 10 am", ADR-183 and ADR-210 in `platform`) are kept and go
 * the same way, as the outcome's backup times.
 */

/** The lead's clock, the one a callback is picked on (`clock_timezone`, else their state's). */
const zoneOf = (card: LeadCard) => card.clock_timezone ?? card.timezone;

/** What is scheduled besides the open callback: the time picked on this call, and the lead's
 *  upcoming appointments, each with its own day and time. */
export function ScheduledOnCall({ card, callId }: { card: LeadCard; callId: string | null }) {
  const picked = usePickedCallback(callId);
  const lead = knownZone(zoneOf(card));
  return (
    <>
      {picked && (
        <p className="mt-1 flex items-start gap-1 text-accent-11" aria-label="Callback time picked">
          <CalendarClock className="mt-px size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">
            Callback picked for {callbackWhen(picked.at, lead).there}
            {picked.backupTimes.length > 0 && ` · or else ${backupTimesWhen(picked.backupTimes, lead, picked.at)}`}
            {picked.purpose.trim() && ` · ${picked.purpose.trim()}`}
            <span className="text-text-muted"> · scheduled when you choose a callback outcome</span>
          </span>
        </p>
      )}
      <UpcomingAppointments key={`upcoming:${card.id}`} leadId={card.id} className="mt-1" />
    </>
  );
}

const ACTION = "inline-flex items-center gap-1 text-2xs font-medium text-accent-11 hover:underline";

/** Callback time, beside the card's Open record. Book appointment left the card (ACL-671): an appointment
 *  is booked from the record, and the rep's screen keeps to calls and callbacks. */
export function ScheduleActions({ card, callId }: { card: LeadCard; callId: string | null }) {
  const picked = usePickedCallback(callId);
  const [picking, setPicking] = React.useState(false);
  return (
    <>
      {callId !== null && (
        <button type="button" className={ACTION} onClick={() => setPicking(true)}>
          <CalendarClock className="size-3" aria-hidden />
          {picked ? "Change callback time" : "Callback time"}
        </button>
      )}
      {/* Mounted only while open. Drawn from in here, the dialog is part of the phone's own popover to
          React, so the docked phone does not close under it. */}
      {picking && callId !== null && (
        <PickCallbackDialog card={card} callId={callId} onClose={() => setPicking(false)} />
      )}
    </>
  );
}

/** The callback's day, time, backup times and purpose, picked on the lead's clock as the outcome
 *  window picks them, and kept for the call. */
function PickCallbackDialog({ card, callId, onClose }: { card: LeadCard; callId: string; onClose: () => void }) {
  const picked = usePickedCallback(callId);
  const callback = useCallbackTime(zoneOf(card));
  const backup = useBackupTime(callback.value, picked?.backupTimes ?? [], backupGapMinutes(card.callback_window_minutes));
  const soon = useCallbackMinutes();
  const { set, setPurpose, value } = callback;
  // Opens on the time picked before, with its backup, else the usual callback wait.
  React.useEffect(() => {
    if (picked) {
      set(wallTime(picked.at, value.zone));
      setPurpose(picked.purpose);
    } else {
      callback.start({ backoff_seconds: null });
    }
    // Once, as it opens: what the rep picks after is theirs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const name = leadName(card) ?? card.phone;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pick a callback time</DialogTitle>
          <DialogDescription>
            {name} · Scheduled when the call ends and you choose a callback outcome. Any other outcome makes
            no callback.
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          aria-label={`When to call ${name} back`}
          onSubmit={(e) => {
            e.preventDefault();
            if (value.instant === null || value.iso === null || !backup.ok) return;
            pickCallback({
              callId,
              at: value.instant,
              purpose: callback.purpose,
              backupTimes: backup.instants,
            });
            onClose();
          }}
        >
          <DialogBody className="flex flex-col gap-3">
            <CallbackPicker
              value={value}
              onChange={set}
              minutes={soon.value}
              onMinutes={soon.set}
              purpose={callback.purpose}
              onPurposeChange={setPurpose}
              windowMinutes={card.callback_window_minutes}
            />
            <BackupPicker first={value} backup={backup} />
          </DialogBody>
          <DialogFooter>
            {picked && (
              <Button
                type="button"
                variant="ghost"
                className="mr-auto text-danger-11"
                onClick={() => {
                  forgetPickedCallback(callId);
                  onClose();
                }}
              >
                Remove time
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={value.iso === null || !backup.ok}>
              Keep this time
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
